'use client';
// GalleryCarousel — a horizontal, scroll-snap strip of an expert's work photos rendered INSIDE the
// category-page card between the business details and the reviews (Founder request 2026-09-22, enabled
// per category via he_categories.card_gallery). Tap/click opens a lightbox over the whole set.
import { useCallback, useEffect, useRef, useState } from 'react';
import LightboxOverlay, { LB_SIZES, LB_WIDTH } from './LightboxOverlay';
import { useDragScroll } from '@/lib/useDragScroll';
import { wideImg } from '@/lib/imgUrl';
import type { SeoCtx } from '@/lib/imageSeo';

export default function GalleryCarousel({ images, altBase, seoCtx, imageTags }: { images: string[]; altBase: string; seoCtx?: SeoCtx; imageTags?: string[][] }) {
  const seoFor = (i: number) => seoCtx ? ({ kind: 'gallery' as const, ctx: { ...seoCtx, tags: imageTags?.[i] || null, n: i + 1 } }) : undefined;
  const [idx, setIdx] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  const strip = useRef<HTMLDivElement>(null);
  useEffect(() => setMounted(true), []);
  useDragScroll(strip);
  // Zoom = the SAME srcset/sizes the dialog uses, warmed + decoded once the strip nears the viewport (bughunter 15/10:
  // a tap fired the zoom request and the photo popped in from 0x0) — a tap now hits cache and opens at its final size.
  const zoomImg = (i: number) => wideImg(images[i], LB_SIZES, LB_WIDTH, seoFor(i));
  const dims = useRef(new Map<number, { w: number; h: number }>());
  useEffect(() => {
    const el = strip.current;
    if (!el || !images.length || typeof IntersectionObserver === 'undefined') return;
    const warm = () => images.forEach((_, i) => {
      const z = zoomImg(i), im = new Image();
      if (z.sizes) im.sizes = z.sizes; if (z.srcSet) im.srcset = z.srcSet; im.src = z.src;
      im.decode().then(() => dims.current.set(i, { w: im.naturalWidth, h: im.naturalHeight })).catch(() => {});
    });
    const io = new IntersectionObserver(es => {
      if (!es.some(e => e.isIntersecting)) return;
      io.disconnect();
      const w = window as any; w.requestIdleCallback ? w.requestIdleCallback(warm, { timeout: 1500 }) : setTimeout(warm, 200);
    }, { rootMargin: '300px' });
    io.observe(el);
    return () => io.disconnect();
  }, [images]); // eslint-disable-line react-hooks/exhaustive-deps
  const close = useCallback(() => setIdx(null), []);
  const go = useCallback((d: number) => setIdx(i => (i === null ? i : (i + d + images.length) % images.length)), [images.length]);
  if (!images.length) return null;
  const scrollBy = (dir: number) => strip.current?.scrollBy({ left: dir * (strip.current.clientWidth * 0.8), behavior: 'smooth' });
  const arrow: React.CSSProperties = { position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: 34, height: 34, borderRadius: '50%', border: '1px solid #e2e8f0', background: 'rgba(255,255,255,.92)', color: '#0d47a1', fontSize: 20, lineHeight: 1, cursor: 'pointer', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,.12)', zIndex: 2 }; // display comes from the class (hidden on touch widths, flex from md) — an inline display would beat Tailwind's `hidden`
  return (
    <div className="relative" dir="rtl" aria-label={`${altBase} - גלריית עבודות`}>
      <div ref={strip} className="flex gap-2 overflow-x-auto" style={{ scrollSnapType: 'x mandatory', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch', paddingBlock: 2 }}>
        {images.map((src, i) => (
          <button key={src + i} type="button" onClick={() => setIdx(i)} aria-label={`${altBase} - תמונה ${i + 1} מתוך ${images.length}`}
            className="flex-shrink-0 rounded-lg overflow-hidden"
            style={{ width: 150, height: 112, scrollSnapAlign: 'start', padding: 0, border: 'none', cursor: 'pointer', background: '#f1f5f9' }}>
            <img {...wideImg(src, '150px', 320, seoFor(i))} {...(seoCtx ? {} : { alt: `${altBase} - עבודה ${i + 1}` })} loading="lazy" decoding="async" referrerPolicy="no-referrer"
              className="object-cover hover:opacity-90 transition-opacity" style={{ width: '100%', height: '100%' }} />
          </button>
        ))}
      </div>
      {images.length > 2 && (<>
        <button dir="ltr" type="button" aria-label="הקודם" onClick={() => scrollBy(1)} style={{ ...arrow, insetInlineStart: -6 }} className="hidden md:flex">‹</button>
        <button dir="ltr" type="button" aria-label="הבא" onClick={() => scrollBy(-1)} style={{ ...arrow, insetInlineEnd: -6 }} className="hidden md:flex">›</button>
      </>)}
      <span className="absolute text-xs font-bold px-2 py-0.5 rounded-full" style={{ insetInlineEnd: 6, top: 6, background: 'rgba(15,23,42,.6)', color: '#fff' }}>{images.length === 1 ? 'תמונה אחת' : `${images.length} תמונות`}</span>
      {mounted && idx !== null && (
        <LightboxOverlay count={images.length} idx={idx} go={go} close={close}
          imgProps={i => ({ ...zoomImg(i), ...(seoCtx ? {} : { alt: `${altBase} - תמונה ${i + 1} מתוך ${images.length}` }) })} />
      )}
    </div>
  );
}
